import type { SceneDocument, SceneOverrides } from "./types";

const MAX_OVERRIDE_BYTES = 64 * 1024;
const blockedKeys = new Set(["__proto__", "prototype", "constructor"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const assertAllowedKeys = (record: Record<string, unknown>, keys: readonly string[], label: string): void => {
  for (const key of Object.keys(record)) {
    if (blockedKeys.has(key) || !keys.includes(key)) throw new Error(`${label} contains unexpected key “${key}”.`);
  }
};

const unitNumber = (value: unknown, label: string): number => {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 1) {
    throw new Error(`${label} must be a finite number between 0 and 1.`);
  }
  return value;
};

export const emptyOverrides = (document: SceneDocument): SceneOverrides => ({
  schemaVersion: 1,
  sceneId: document.id,
  layers: {},
  groupDepths: {},
});

export const parseOverrides = (source: string, document: SceneDocument): SceneOverrides => {
  if (new TextEncoder().encode(source).byteLength > MAX_OVERRIDE_BYTES) {
    throw new Error("Override JSON is larger than 64 KB.");
  }

  let value: unknown;
  try {
    value = JSON.parse(source) as unknown;
  } catch {
    throw new Error("Override JSON is malformed.");
  }
  if (!isRecord(value)) throw new Error("Override JSON must be an object.");
  assertAllowedKeys(value, ["schemaVersion", "sceneId", "layers", "groupDepths"], "Override");
  if (value.schemaVersion !== 1) throw new Error("Unsupported override schema.");
  if (value.sceneId !== document.id) throw new Error("Overrides belong to a different scene.");
  if (!isRecord(value.layers) || !isRecord(value.groupDepths)) {
    throw new Error("Overrides must include layer and group-depth objects.");
  }

  const layerIds = new Set(document.layers.map((layer) => layer.id));
  const groupIds = new Set(document.layers.map((layer) => layer.motionGroup));
  const layers: SceneOverrides["layers"] = {};
  for (const [id, raw] of Object.entries(value.layers)) {
    if (blockedKeys.has(id) || !layerIds.has(id)) throw new Error(`Unknown layer ID “${id}”.`);
    if (!isRecord(raw)) throw new Error(`Layer override “${id}” must be an object.`);
    assertAllowedKeys(raw, ["visible", "opacity"], `Layer override ${id}`);
    const next: { visible?: boolean; opacity?: number } = {};
    if ("visible" in raw) {
      if (typeof raw.visible !== "boolean") throw new Error(`${id}.visible must be Boolean.`);
      next.visible = raw.visible;
    }
    if ("opacity" in raw) next.opacity = unitNumber(raw.opacity, `${id}.opacity`);
    layers[id] = next;
  }

  const groupDepths: Record<string, number> = {};
  for (const [id, raw] of Object.entries(value.groupDepths)) {
    if (blockedKeys.has(id) || !groupIds.has(id)) throw new Error(`Unknown motion group “${id}”.`);
    groupDepths[id] = unitNumber(raw, `${id}.depth`);
  }

  return { schemaVersion: 1, sceneId: document.id, layers, groupDepths };
};

export const serializeOverrides = (overrides: SceneOverrides): string => JSON.stringify(overrides, null, 2);

export const cloneOverrides = (overrides: SceneOverrides): SceneOverrides =>
  JSON.parse(JSON.stringify(overrides)) as SceneOverrides;
